import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { X, Trash } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/date";

export default function AdminRewards() {
  const { t } = useLang();
  const [rewards, setRewards] = useState([]);
  const [sendingReward, setSendingReward] = useState(null);
  const [sendForm, setSendForm] = useState({ amazon_code: "", hotel_booking_ref: "", treatment_credit_ref: "", note: "" });

  const load = () => api.get("/admin/rewards").then((r) => setRewards(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/rewards/${id}/approve`); toast.success("Approved"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openSend = (r) => {
    setSendingReward(r);
    setSendForm({ amazon_code: "", hotel_booking_ref: "", treatment_credit_ref: "", note: "" });
  };

  const send = async () => {
    try {
      await api.post(`/admin/rewards/${sendingReward.id}/send`, sendForm);
      toast.success("Reward sent");
      setSendingReward(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete reward "${r.label_en}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/rewards/${r.id}`);
      toast.success("Reward deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-serif-display text-4xl">{t.admin.rewards}</h1>
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        {rewards.length === 0 ? (
          <div className="p-10 text-center text-sm text-white/60">No rewards yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
              <tr>
                <th className="text-left p-4">Ambassador</th>
                <th className="text-left p-4">Referral #</th>
                <th className="text-left p-4">Reward</th>
                <th className="text-left p-4">Created</th>
                <th className="text-left p-4">Sent</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((r) => (
                <tr key={r.id} data-testid={`rew-row-${r.id}`} className="border-b border-white/5 last:border-0">
                  <td className="p-4">{r.ambassador?.name}<br/><span className="text-white/50 text-xs">{r.ambassador?.email}</span></td>
                  <td className="p-4">#{r.referral_number} <span className="text-xs text-white/50">({r.level})</span></td>
                  <td className="p-4">{r.label_en}</td>
                  <td className="p-4 text-xs text-white/70">{formatDateTime(r.created_at)}</td>
                  <td className="p-4 text-xs text-white/70">{r.sent_at ? formatDateTime(r.sent_at) : "—"}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full bg-white/5 text-xs">{r.status}</span></td>
                  <td className="p-4">
                    <div className="flex gap-2 items-center">
                      {r.status === "pending" && (
                        <button data-testid={`approve-reward-${r.id}`} onClick={() => approve(r.id)} className="px-3 py-1.5 rounded-full border border-white/20 text-xs">{t.admin.approve}</button>
                      )}
                      {(r.status === "pending" || r.status === "approved") && (
                        <button data-testid={`send-reward-${r.id}`} onClick={() => openSend(r)} className="px-3 py-1.5 rounded-full bg-[#f4a261] text-[#0b1521] text-xs">Send</button>
                      )}
                      <button data-testid={`delete-reward-${r.id}`} onClick={() => remove(r)} title="Delete reward" className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10">
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sendingReward && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSendingReward(null)}>
          <div className="w-full max-w-lg rounded-3xl bg-[#122031] p-8 relative border border-white/10" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSendingReward(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center"><X size={14} /></button>
            <h2 className="font-serif-display text-3xl">Send reward</h2>
            <p className="text-sm text-white/60 mt-2">{sendingReward.label_en}</p>
            <div className="mt-6 space-y-3">
              {sendingReward.type === "amazon" && (
                <input data-testid="send-amazon-code" placeholder="Amazon gift card code" value={sendForm.amazon_code} onChange={(e) => setSendForm((f) => ({ ...f, amazon_code: e.target.value }))} className="input" />
              )}
              {sendingReward.type === "hotel" && (
                <input data-testid="send-hotel-ref" placeholder="Hotel booking reference" value={sendForm.hotel_booking_ref} onChange={(e) => setSendForm((f) => ({ ...f, hotel_booking_ref: e.target.value }))} className="input" />
              )}
              {sendingReward.type === "treatment" && (
                <input data-testid="send-treatment-ref" placeholder="Treatment credit reference" value={sendForm.treatment_credit_ref} onChange={(e) => setSendForm((f) => ({ ...f, treatment_credit_ref: e.target.value }))} className="input" />
              )}
              <textarea placeholder="Internal note (optional)" rows={2} value={sendForm.note} onChange={(e) => setSendForm((f) => ({ ...f, note: e.target.value }))} className="input resize-none" />
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setSendingReward(null)} className="px-5 py-2.5 rounded-full border border-white/20 text-sm">{t.common.cancel}</button>
              <button data-testid="confirm-send-reward-btn" onClick={send} className="px-5 py-2.5 rounded-full bg-[#f4a261] text-[#0b1521] text-sm">Send reward</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input { width:100%; padding:11px 14px; border-radius:10px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.15); color:white; font-size:14px; outline:none; }
        .input:focus { border-color:#f4a261; }
      `}</style>
    </div>
  );
}
